import { IButtonStates } from './types';
import { IThemeSemantics } from '../../../styling';
import { createThemeCompiler } from '../../../utils';

interface IButtonStyleBuilderVariables {
    // Default
    background: string;
    border: string;
    boxShadow?: string;
    text: string;

    // Hovered
    hoveredBackground: string;
    hoveredBorder: string;
    hoveredBoxShadow?: string;
    hoveredText?: string;

    // Disabled
    disabledBackground?: string;
    disabledBorder?: string;
    disabledBoxShadow?: string;
    disabledText?: string;

    // Active
    activeBackground?: string;
    activeBorder?: string;
    activeBoxShadow?: string;
    activeText?: string;

    // Focused
    focusedBackground?: string;
    focusedBorder?: string;
    focusedBoxShadow?: string;
    focusedText?: string;
}

function buttonStyleBuilder(variables: IButtonStyleBuilderVariables, semantics: IThemeSemantics): IButtonStates {
    const {
        background,
        border,
        boxShadow = '',
        text,
        hoveredBackground,
        hoveredBorder,
        hoveredBoxShadow = '',
        hoveredText = text,
        activeBackground = hoveredBackground,
        activeBorder = hoveredBorder,
        activeBoxShadow = hoveredBoxShadow,
        activeText = hoveredText,
        disabledBackground = semantics.palette.neutralLight,
        disabledBorder = semantics.palette.neutralLight,
        disabledBoxShadow = '',
        disabledText = semantics.palette.neutralSecondaryAlt,
        focusedBackground = activeBackground,
        focusedBorder = activeBorder,
        focusedBoxShadow = activeBoxShadow,
        focusedText = activeText
    } = variables;
    return {
        active: {
            background: activeBackground,
            border: activeBorder,
            boxShadow: activeBoxShadow,
            text: activeText
        },
        default: {
            background,
            border,
            boxShadow,
            text
        },
        disabled: {
            background: disabledBackground,
            border: disabledBorder,
            boxShadow: disabledBoxShadow,
            text: disabledText
        },
        focussed: {
            background: focusedBackground,
            border: focusedBorder,
            boxShadow: focusedBoxShadow,
            text: focusedText
        },
        hovered: {
            background: hoveredBackground,
            border: hoveredBorder,
            boxShadow: hoveredBoxShadow,
            text: hoveredText
        }
    };
}


export const buttonThemeCompiler = createThemeCompiler((semantics) => {
    const { palette, stroke, borderRadius } = semantics;
    return {
        button: {
            colors: {
                action: buttonStyleBuilder({
                    background: palette.transparent,
                    border: palette.transparent,
                    text: palette.neutralPrimary,
                    disabledBackground: palette.transparent,
                    disabledBorder: palette.transparent,
                    disabledText: palette.neutralSecondaryAlt,
                    hoveredBackground: palette.transparent,
                    hoveredBorder: palette.transparent,
                    hoveredText: palette.themePrimary
                }, semantics),
                plain: buttonStyleBuilder({
                    background: palette.transparent,
                    border: palette.transparent,
                    text: palette.neutralPrimary,
                    disabledBackground: palette.transparent,
                    disabledBorder: palette.transparent,
                    disabledText: palette.neutralSecondaryAlt,
                    focusedText: palette.themePrimary,
                    hoveredBackground: palette.transparent,
                    hoveredBorder: palette.transparent,
                    hoveredText: palette.neutralPrimary
                }, semantics),
                default: buttonStyleBuilder({
                    activeBackground: '#eaebf1',
                    background: '#EAEBF1',
                    disabledBackground: '#F0F0F7',
                    hoveredBackground: '#CDCFD7',
                    activeBorder: '#eaebf1',
                    border: '#eaebf1',
                    disabledBorder: '#F0F0F7',
                    hoveredBorder: '#CDCFD7',
                    text: '#676E7B',
                    hoveredText: '#676E7B',
                    disabledText: '#B3B6BD',
                    focusedBoxShadow: 'none',
                    focusedBackground: '#CDCFD7'
                }, semantics),
                negative: buttonStyleBuilder({
                    background: palette.red,
                    focusedBackground: palette.red,
                    activeBackground: palette.red,
                    hoveredBackground: '#C35E64',
                    disabledBackground: '#D7989E',
                    border: palette.red,
                    hoveredBorder: '#C35E64',
                    focusedBorder: palette.red,
                    focusedBoxShadow: 'none',
                    activeBorder: palette.red,
                    disabledBorder: '#D7989E',
                    text: palette.white,
                    focusedText: palette.white,
                    activeText: palette.white,
                    hoveredText: '#EDCFD1',
                    disabledText: palette.white
                }, semantics),
                negativeOutline: buttonStyleBuilder({
                    background: palette.transparent,
                    focusedBackground: palette.red,
                    focusedBoxShadow: 'none',
                    activeBackground: '#AD5359',
                    hoveredBackground: '#C35E64',
                    disabledBackground: palette.neutralQuaternary,
                    border: palette.red,
                    hoveredBorder: '#C35E64',
                    focusedBorder: '#AD5359',
                    activeBorder: '#AD5359',
                    disabledBorder: palette.neutralQuaternary,
                    text: palette.red,
                    focusedText: palette.white,
                    activeText: palette.white,
                    hoveredText: palette.white,
                    disabledText: '#A8AFBD'
                }, semantics),
                positive: buttonStyleBuilder({
                    background: palette.green,
                    focusedBackground: palette.green,
                    focusedBoxShadow: 'none',
                    activeBackground: palette.green,
                    hoveredBackground: '#449863',
                    disabledBackground: '#87BC9D',
                    border: palette.green,
                    hoveredBorder: '#449863',
                    focusedBorder: palette.green,
                    activeBorder: palette.green,
                    disabledBorder: '#87BC9D',
                    text: palette.white,
                    focusedText: palette.white,
                    activeText: palette.white,
                    hoveredText: '#C7E0D0',
                    disabledText: palette.white
                }, semantics),
                positiveOutline: buttonStyleBuilder({
                    background: palette.transparent,
                    focusedBackground: palette.green,
                    focusedBoxShadow: 'none',
                    activeBackground: palette.greenLight,
                    hoveredBackground: '#449863',
                    disabledBackground: palette.neutralQuaternary,
                    border: palette.green,
                    hoveredBorder: '#449863',
                    focusedBorder: palette.greenLight,
                    activeBorder: palette.greenLight,
                    disabledBorder: palette.neutralQuaternary,
                    text: palette.green,
                    focusedText: palette.white,
                    activeText: '',
                    hoveredText: palette.white,
                    disabledText: '#A8AFBD'
                }, semantics),
                primary: buttonStyleBuilder({
                    background: palette.themePrimary,
                    focusedBackground: palette.themePrimary,
                    focusedBoxShadow: 'none',
                    activeBackground: palette.themePrimary,
                    hoveredBackground: '#3291E0',
                    disabledBackground: '#7CB7EC',
                    border: palette.themePrimary,
                    hoveredBorder: '#3291E0',
                    focusedBorder: palette.themePrimary,
                    activeBorder: palette.themePrimary,
                    disabledBorder: '#7CB7EC',
                    text: palette.white,
                    focusedText: palette.white,
                    activeText: palette.white,
                    hoveredText: '#C2DEF6',
                    disabledText: palette.white
                }, semantics),
                primaryOutline: buttonStyleBuilder({
                    background: palette.transparent,
                    focusedBackground: palette.neutralTertiary,
                    focusedBoxShadow: 'none',
                    activeBackground: palette.neutralTertiary,
                    hoveredBackground: palette.neutralQuaternary,
                    disabledBackground: palette.neutralQuaternary,
                    border: palette.neutralPrimaryAlt,
                    hoveredBorder: palette.neutralQuaternary,
                    focusedBorder: palette.neutralTertiary,
                    activeBorder: palette.neutralTertiary,
                    disabledBorder: palette.neutralQuaternary,
                    text: palette.neutralPrimaryAlt,
                    focusedText: palette.neutralPrimaryAlt,
                    activeText: palette.white,
                    hoveredText: palette.neutralPrimaryAlt,
                    disabledText: '#A8AFBD'
                }, semantics),
                secondary: buttonStyleBuilder({
                    background: palette.transparent,
                    focusedBackground: palette.neutralTertiary,
                    focusedBoxShadow: 'none',
                    activeBackground: palette.neutralTertiary,
                    hoveredBackground: palette.neutralQuaternary,
                    disabledBackground: palette.neutralQuaternary,
                    border: palette.neutralPrimaryAlt,
                    hoveredBorder: palette.neutralQuaternary,
                    focusedBorder: palette.neutralTertiary,
                    activeBorder: palette.neutralTertiary,
                    disabledBorder: palette.neutralQuaternary,
                    text: palette.neutralPrimaryAlt,
                    focusedText: palette.neutralPrimaryAlt,
                    activeText: palette.white,
                    hoveredText: palette.neutralPrimaryAlt,
                    disabledText: '#A8AFBD'
                }, semantics),
                warning: buttonStyleBuilder({
                    background: palette.orangeDark,
                    focusedBackground: palette.orangeDark,
                    focusedBoxShadow: 'none',
                    activeBackground: palette.orangeDark,
                    hoveredBackground: '#BC934D',
                    disabledBackground: '#CFB280',
                    border: palette.orangeDark,
                    hoveredBorder: '#BC934D',
                    focusedBorder: palette.orangeDark,
                    activeBorder: palette.orangeDark,
                    disabledBorder: '#CFB280',
                    text: palette.white,
                    focusedText: palette.white,
                    activeText: palette.white,
                    hoveredText: palette.white,
                    disabledText: palette.white
                }, semantics),
                warningOutline: buttonStyleBuilder({
                    background: palette.transparent,
                    focusedBackground: palette.orangeDark,
                    focusedBoxShadow: 'none',
                    activeBackground: palette.orangeDark,
                    hoveredBackground: '#FFF6E9',
                    disabledBackground: palette.neutralQuaternary,
                    border: palette.orangeDark,
                    hoveredBorder: palette.orangeDark,
                    focusedBorder: palette.orangeDark,
                    activeBorder: palette.orangeDark,
                    disabledBorder: palette.neutralQuaternary,
                    text: palette.neutralDark,
                    focusedText: palette.white,
                    activeText: palette.white,
                    hoveredText: palette.neutralDark,
                    disabledText: '#A8AFBD'
                }, semantics)
            },
            composites: {
                borderRadius: borderRadius.curvedXLarge,
                borderWidth: stroke.thin
            }
        }
    };
});
